import { useQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";

import { fetchMe, type CurrentUser } from "../api/client";

/**
 * Returns the current user from /auth/me, joined with the OIDC session.
 * The OIDC session tells us *whether* the user is logged in; the API tells us
 * *what role* they have and whether their account is active. Both are required
 * — never trust the token alone for role decisions, since deactivation is a
 * server-side gate that fires regardless of token validity.
 *
 * The query key is intentionally STABLE (doesn't include the access_token).
 * Keying on the token would refetch on every silent renew (~5min), which is
 * wasteful and discards cache (B-21). The token is read inside queryFn at
 * call time, so the latest token is always used.
 */
export function useCurrentUser() {
  const auth = useAuth();
  const accessToken = auth.user?.access_token;
  const userKey = auth.user?.profile?.sub;

  const query = useQuery<CurrentUser>({
    queryKey: ["auth", "me", userKey],
    queryFn: () => fetchMe(auth.user!.access_token),
    // Gate on isAuthenticated, NOT just !!accessToken. oidc-client-ts
    // exposes `access_token` from the stored User even when the token
    // has expired (`isAuthenticated` flips to false on expiry, but the
    // token string remains readable). Without this gate, an expired
    // session in localStorage triggers /auth/me 401s on every page
    // load until the user manually clears storage.
    enabled: auth.isAuthenticated && !!accessToken && !auth.isLoading,
    retry: false,
    staleTime: 60_000,
  });

  // ADR-0010 PRM-rendered login means the browser never touches
  // Authentik's web UI, so there's no Authentik browser session
  // cookie. Calling auth.signoutRedirect() sends the browser to
  // /application/o/prm/end-session/, which (without a session cookie)
  // bounces to /flows/-/default/authentication/?next=… ("sign in
  // first to sign out"); the public nginx blocks that flow path, so
  // the user lands on the SPA shell still holding tokens. Local-only
  // sign-out: clear oidc-client-ts storage, hard-nav to /login.
  const signOut = async (): Promise<void> => {
    await auth.removeUser();
    window.location.assign("/login");
  };

  return {
    ...query,
    isAuthenticated: auth.isAuthenticated,
    isAuthLoading: auth.isLoading,
    signoutRedirect: signOut,
    accessToken,
  };
}
